import React from 'react';
import { Select, Table, Tooltip, TableColumnsType } from 'antd';
import { PermissionValue, RoleModel, SectionsRow } from './utils/types';
import ArrowDown from '@/assets/icons/arrow-down-role.svg';
import { PencilIcon } from '@/assets/icons/icons';
import { getBackground } from './utils/permissions';
import { FeaturePermissionMatrix } from '@/apis/types';

const { Option } = Select;

/**
 * Props for the SectionsTable component
 */
interface Props {
  /** Array of section/feature rows to display in the table */
  rows: SectionsRow[];
  /** Array of roles to display as columns */
  roles: RoleModel[];
  /** Function to determine if a role is locked (admin, member, viewer) and cannot be edited */
  isBaseLocked: (roleName: string) => boolean;
  /** Function to get permission options available for a specific feature */
  optionsForRow: (
    featureName: string
  ) => { value: PermissionValue; label: string }[];
  /** Callback when a permission value is changed for a feature-role combination */
  onChange: (
    featureName: string,
    role: RoleModel,
    val: PermissionValue
  ) => void;
  /** Callback when the edit icon for a role header is clicked */
  onEditRole: (role: RoleModel) => void;
  /** Function to determine if a role header can show an edit icon based on role index */
  canEditRoleHeader: (index: number) => boolean;
  /** Optional width for the first column (feature names) */
  firstColumnWidth?: number;
  /** Whether the view is on desktop (affects column fixed behavior) */
  isDesktop: boolean;
  /** Current user's permissions for the roles & permissions feature */
  permission: FeaturePermissionMatrix;
}

const FIRST_COL_W = 210;
const COL_W = 180;

/**
 * Renders a permissions matrix table for managing role-based access control.
 *
 * Displays features/sections as rows and roles as columns, with Select dropdowns
 * to configure permission levels (VIEW, EDIT, CREATE, NO_ACCESS) for each combination.
 */
const SectionsTable: React.FC<Props> = ({
  isDesktop,
  rows,
  roles,
  isBaseLocked,
  optionsForRow,
  onChange,
  onEditRole,
  canEditRoleHeader,
  permission,
}) => {
  const baseCol: TableColumnsType<SectionsRow>[number] = {
    title: '',
    dataIndex: 'feature_name',
    key: 'feature_name',
    fixed: isDesktop ? 'left' : undefined,
    width: FIRST_COL_W,
    minWidth: FIRST_COL_W,
  };

  const roleCols: TableColumnsType<SectionsRow> = roles.map((role, idx) => ({
    dataIndex: role.id,
    key: role.id,
    width: COL_W,
    minWidth: COL_W,
    onHeaderCell: () => ({ style: { minWidth: COL_W } }),
    onCell: () => ({ style: { minWidth: COL_W } }),
    title: (
      <div className='ft-roleHeader'>
        <span className='ft-roleName'>{role.name}</span>
        {canEditRoleHeader(idx) && (
          <Tooltip title={`Edit ${role.name}`}>
            <span
              role='button'
              aria-label='edit-role'
              className='ft-editHeaderIcon'
              onClick={() => onEditRole(role)}
            >
              <PencilIcon width={12} height={12} color='var(--text-primary)' />
            </span>
          </Tooltip>
        )}
      </div>
    ),
    render: (val: PermissionValue, record: SectionsRow) => {
      const hasWrite = Boolean(permission?.is_update || permission?.is_create);
      const disabled = isBaseLocked(role.name) || !hasWrite;
      const opts = optionsForRow(record.feature_name);
      const isNoAccess = val === 'NO_ACCESS';
      return (
        <div className='font-black-color section-table'>
          <Select
            disabled={disabled}
            className={
              !permission.is_create && !permission.is_update
                ? 'black-border'
                : !isBaseLocked(role.name)
                  ? 'b-none'
                  : 'black-border'
            }
            style={{
              width: '100%',
              background: getBackground(
                role.name,
                isNoAccess,
                isBaseLocked,
                permission
              ),
              fontSize: 12,
              borderRadius: 4,
              cursor: disabled ? 'not-allowed' : 'pointer',
            }}
            suffixIcon={<img src={ArrowDown} alt='dropdown' />}
            value={val as PermissionValue}
            onChange={(v) => onChange(record.feature_name, role, v)}
          >
            {opts.map((o) => (
              <Option key={o.value as string} value={o.value}>
                {o.label}
              </Option>
            ))}
          </Select>
        </div>
      );
    },
  }));

  const columns: TableColumnsType<SectionsRow> = [baseCol, ...roleCols];
  const totalWidth = FIRST_COL_W + roles.length * COL_W;

  return (
    <Table<SectionsRow>
      columns={columns}
      dataSource={rows ?? []}
      pagination={false}
      className='custom-roles-table w-full'
      scroll={{ x: totalWidth }}
      sticky
      tableLayout='fixed'
      rowKey='key'
    />
  );
};

export default SectionsTable;
